/* ==========================================================================
   FNDRY — Video
   1. Headline: characters are split in JS and filled sequentially as the
      section crosses the viewport. Nothing is held at a hidden state by JS —
      the CSS default is "fully filled", so a thrown script leaves the
      headline perfectly legible.
   2. Product: hover image zones, slide-up quick add, cart-derived added state.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ---------------------------------------------------------------- utils */

  function clamp01(n) {
    return n < 0 ? 0 : n > 1 ? 1 : n;
  }

  /* ------------------------------------------------------- headline fill */

  function buildHeadline(root) {
    var head = root.querySelector('[data-fv-headline]');
    if (!head) return null;

    var segs = head.querySelectorAll('[data-fv-fill]');
    if (!segs.length) return null;

    var chars = [];
    var accentRange = null;

    Array.prototype.forEach.call(segs, function (seg) {
      var isAccent = seg.hasAttribute('data-fv-accent');
      var first = chars.length;
      var parts = String(seg.textContent).split(/(\s+)/);

      seg.textContent = '';

      parts.forEach(function (part) {
        if (!part) return;

        if (/^\s+$/.test(part)) {
          seg.appendChild(document.createTextNode(' '));
          return;
        }

        var word = document.createElement('span');
        word.className = 'fndry-video__word';

        for (var i = 0; i < part.length; i++) {
          var c = document.createElement('span');
          c.className = 'fndry-video__char';
          c.textContent = part.charAt(i);
          word.appendChild(c);
          chars.push(c);
        }

        seg.appendChild(word);
      });

      if (isAccent && chars.length > first) {
        accentRange = { first: first, last: chars.length - 1 };
      }
    });

    var n = chars.length;
    if (!n) return null;

    // Softness of the wet edge, in normalised units. ~3 characters wide,
    // clamped so very short and very long headlines both read well.
    var soft = Math.min(0.16, Math.max(0.05, 3 / n));
    var span = 1 - soft;

    function startOf(i) {
      return (n > 1 ? i / (n - 1) : 0) * span;
    }

    chars.forEach(function (c, i) {
      c.style.setProperty('--cs', startOf(i).toFixed(4));
    });

    head.style.setProperty('--fv-sharp', (1 / soft).toFixed(3));
    head.setAttribute('data-fv-ready', 'true');

    var accent = null;
    if (accentRange) {
      var a0 = startOf(accentRange.first);
      var a1 = startOf(accentRange.last) + soft;
      accent = { from: a0, to: a1 > a0 ? a1 : a0 + 0.0001 };
    }

    return {
      el: head,
      accent: accent,
      chars: chars,
      starts: chars.map(function (c, i) { return parseFloat(startOf(i).toFixed(4)); }),
      sharp: parseFloat((1 / soft).toFixed(3))
    };
  }

  function setupHeadline(root) {
    var built = buildHeadline(root);
    if (!built) return;

    var head = built.el;
    var accent = built.accent;

    /* P15 2026-10-23: only a changed value is written.
       P15 sitewide 2026-10-23: and only to the characters whose fill changes.
       Each character used to work out its own colour from one --fv-p on the
       headline, so every frame of the fill restyled all of them (130-odd on
       the home page) - the largest scroll cost left on the home page. The
       same sum now runs here, with the same rounded numbers the stylesheet
       used, and each character gets its own --fv-c only when it moves; the
       accent underline gets --fv-accent-p on itself, not on the headline. */
    var chars = built.chars;
    var starts = built.starts;
    var sharp = built.sharp;
    var rule = head.querySelector('.fndry-video__rule');
    var fills = [];
    var painted = null;
    var ruled = null;
    function paint(p) {
      var value = p.toFixed(4);
      if (value === painted) return;
      painted = value;
      var pv = parseFloat(value);
      for (var i = 0; i < chars.length; i++) {
        var c = clamp01((pv - starts[i]) * sharp).toFixed(4);
        if (c !== fills[i]) {
          fills[i] = c;
          chars[i].style.setProperty('--fv-c', c);
        }
      }
      var ap = accent ? clamp01((p - accent.from) / (accent.to - accent.from)).toFixed(4) : value;
      if (ap !== ruled) {
        ruled = ap;
        (rule || head).style.setProperty('--fv-accent-p', ap);
      }
    }

    if (REDUCED.matches) {
      paint(1);
      return;
    }

    var start = (parseFloat(root.getAttribute('data-fv-start')) || 50) / 100;
    var end = (parseFloat(root.getAttribute('data-fv-end')) || 90) / 100;
    if (end <= start) end = start + 0.01;

    var current = 0;
    var target = 0;
    var latched = false;
    var running = false;

    paint(0);

    function measure() {
      var rect = root.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;

      // Fully out of view in either direction: re-arm.
      if (rect.bottom <= 0 || rect.top >= vh) {
        latched = false;
        target = rect.top >= vh ? 0 : 1;
        return;
      }

      // How much of the section is on screen, normalised against whichever is
      // smaller — the section or the viewport — so a section taller than the
      // viewport can still reach 1.
      var visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
      var denom = Math.min(rect.height, vh) || 1;
      var coverage = clamp01(visible / denom);

      var raw = clamp01((coverage - start) / (end - start));

      if (raw >= 1) latched = true;
      target = latched ? 1 : raw;
    }

    function tick() {
      var delta = target - current;
      current += delta * 0.22;
      if (Math.abs(target - current) < 0.0015) current = target;

      paint(current);

      if (current !== target) {
        window.requestAnimationFrame(tick);
      } else {
        running = false;
      }
    }

    var queued = false;
    /* P15 2026-10-23: scroll frames only measure while the section is within a
       screen of the viewport; further away the headline is fully out of view
       and measure() could only re-arm it, which the observer's own call does
       on the way out. */
    var near = !('IntersectionObserver' in window);
    var io = null;

    function settle() {
      measure();
      if (!running && current !== target) {
        running = true;
        window.requestAnimationFrame(tick);
      }
    }

    function onScroll() {
      if (!near || queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        settle();
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (!near) {
      io = new IntersectionObserver(function (entries) {
        near = entries[entries.length - 1].isIntersecting;
        settle();
      }, { rootMargin: '100% 0px 100% 0px' });
      io.observe(root);
    } else {
      onScroll();
    }

    root.fndryVideoTeardown = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (io) io.disconnect();
    };
  }

  /* ------------------------------------------------------- background video */

  /* P15 sitewide 2026-10-23: the clip used to autoplay from page load, far
     below the fold - its poster and first frames competed with the page's own
     loading. Now its poster arrives a screen and a half ahead of it, it plays
     from half a screen ahead (so it is already moving when she reaches it,
     as before) and it rests when she has scrolled well past. */
  function setupVideo(root) {
    var video = root.querySelector('video[data-fv-autoplay]');
    if (!video) return;

    var poster = video.getAttribute('data-poster');
    var warmed = false;

    function warm() {
      if (warmed) return;
      warmed = true;
      if (poster && !video.getAttribute('poster')) video.setAttribute('poster', poster);
      video.preload = 'metadata';
    }

    function play() {
      warm();
      var p = video.play();
      if (p && p.catch) p.catch(function () { /* autoplay refused: the poster stays */ });
    }

    if (!('IntersectionObserver' in window)) {
      play();
      return;
    }

    new IntersectionObserver(function (entries, obs) {
      if (!entries[entries.length - 1].isIntersecting) return;
      warm();
      obs.disconnect();
    }, { rootMargin: '150% 0px 150% 0px' }).observe(video);

    new IntersectionObserver(function (entries) {
      if (entries[entries.length - 1].isIntersecting) play();
      else if (warmed && !video.paused) video.pause();
    }, { rootMargin: '50% 0px 50% 0px' }).observe(video);
  }

  /* ------------------------------------------------------------- product */

  function setupImageZones(root) {
    var media = root.querySelector('[data-fv-media]');
    if (!media) return;

    var layers = media.querySelectorAll('[data-fv-layer]');
    if (layers.length < 2) return;

    function show(index) {
      Array.prototype.forEach.call(layers, function (layer, i) {
        layer.classList.toggle('is-on', i === index);
      });
    }

    function onMove(event) {
      if (!FINE.matches) return;
      var rect = media.getBoundingClientRect();
      if (!rect.width) return;
      show(event.clientX - rect.left > rect.width / 2 ? 1 : 0);
    }

    media.addEventListener('pointermove', onMove);
    media.addEventListener('pointerleave', function () { show(0); });

    // Keyboard users must never land on a stuck secondary image.
    root.addEventListener('focusin', function () { show(0); });
  }

  function setupQuickAdd(root) {
    var buttons = root.querySelectorAll('[data-fv-add]');
    if (!buttons.length) return;

    var variantId = String(root.getAttribute('data-fv-variant') || '');
    if (!variantId) return;

    var addUrl = root.getAttribute('data-fv-add-url') || '/cart/add.js';
    var cartUrl = root.getAttribute('data-fv-cart-url') || '/cart.js';
    var live = root.querySelector('[data-fv-live]');

    function setAdded(state) {
      Array.prototype.forEach.call(buttons, function (btn) {
        btn.classList.toggle('is-added', state);
        btn.disabled = !!state;
        btn.setAttribute('aria-disabled', state ? 'true' : 'false');
      });
    }

    function syncFromCart() {
      return fetch(cartUrl, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var found = (cart.items || []).some(function (item) {
            return String(item.variant_id) === variantId;
          });
          setAdded(found);
          return cart;
        })
        .catch(function () { /* leave the button in its current state */ });
    }

    function add(button) {
      if (button.disabled) return;
      button.disabled = true;

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          return fetch(cartUrl, { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          setAdded(true);
          if (live) live.textContent = button.getAttribute('data-fv-added-message') || 'Added to cart';
          document.dispatchEvent(new CustomEvent('fndry:cart:updated', {
            detail: { cart: cart, variantId: variantId, source: 'fndry-video' }
          }));
        })
        .catch(function () {
          button.disabled = false;
          if (live) live.textContent = 'Could not add to cart. Try again.';
        });
    }

    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', function () { add(btn); });
    });

    document.addEventListener('fndry:cart:refresh', syncFromCart);
    document.addEventListener('fndry:cart:updated', function (event) {
      var cart = event.detail && event.detail.cart;
      if (!cart) return syncFromCart();
      var found = (cart.items || []).some(function (item) {
        return String(item.variant_id) === variantId;
      });
      setAdded(found);
    });

    syncFromCart();
  }

  /* ---------------------------------------------------------------- init */

  function init(root) {
    if (!root || root.getAttribute('data-fv-init') === 'true') return;
    root.setAttribute('data-fv-init', 'true');

    try {
      setupVideo(root);
    } catch (e) {
      var v = root.querySelector('video[data-fv-autoplay]');
      if (v) {
        if (v.getAttribute('data-poster')) v.setAttribute('poster', v.getAttribute('data-poster'));
        v.preload = 'metadata';
        try {
          var pr = v.play();
          if (pr && pr.catch) pr.catch(function () {});
        } catch (e2) { /* the poster stays */ }
      }
    }

    try {
      setupHeadline(root);
      setupImageZones(root);
      setupQuickAdd(root);
    } catch (error) {
      // Restore the revealed state so nothing is ever stranded mid-fill.
      var head = root.querySelector('[data-fv-headline]');
      if (head) {
        head.style.setProperty('--fv-p', '1');
        head.style.setProperty('--fv-accent-p', '1');
        Array.prototype.forEach.call(head.querySelectorAll('.fndry-video__char, .fndry-video__rule'), function (el) {
          el.style.removeProperty('--fv-c');
          el.style.removeProperty('--fv-accent-p');
        });
      }
      if (window.console && console.warn) console.warn('[fndry-video]', error);
    }
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-fndry-video]');
    Array.prototype.forEach.call(nodes, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section) return;
    var root = section.querySelector ? section.querySelector('[data-fndry-video]') : null;
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target;
    if (!section || !section.querySelector) return;
    var root = section.querySelector('[data-fndry-video]');
    if (root && typeof root.fndryVideoTeardown === 'function') root.fndryVideoTeardown();
  });
})();